import dlv from 'dlv';
import { FieldValues } from 'react-hook-form';
import { PerilUpdateRequest, PolicyUpdateRequest } from '../../../types/policy';
import { FormBuilder, FormShape, SaveOptions } from '../types';
import {
	getPathsForForms,
	updatePerilRequestBuilder,
	updateRequestBuilder,
} from '../utils';

const draftPolicyUpdateBuilder = (
	dirtyFields: { [key: string]: boolean },
	formValues: FieldValues,
	fields: FormShape<FormBuilder>,
	primaryFormName: string,
	{
		addExposure = undefined,
		removeExposure = undefined,
		addPeril = undefined,
		removePeril = undefined,
	} = {} as SaveOptions,
	timezone = 'America/Los_Angeles',
	perilLimitsHidden = false,
) => {
	const paths = getPathsForForms(fields, primaryFormName);
	let updateObject = {
		updateExposures: [],
		fieldValues: {},
	} as PolicyUpdateRequest;

	paths.forEach((key) => {
		if (Object.keys(dlv(dirtyFields, key, {})).length > 0) {
			let newUpdatedObject;
			const currDirtyFields = { [key]: dlv(dirtyFields, key, {}) };
			const currFormValues = { [key]: dlv(formValues, key, {}) };
			const currFields = {
				[key]: dlv(fields, key, {}),
			} as FormShape<FormBuilder>;

			if (primaryFormName === key) {
				newUpdatedObject = updateRequestBuilder<PolicyUpdateRequest>(
					currDirtyFields,
					currFormValues,
					currFields,
					key,
					timezone,
				);
				updateObject = { ...updateObject, ...newUpdatedObject };
			}
			// This is a peril update
			else {
				if (key.split('.').length > 1) {
					newUpdatedObject = updatePerilRequestBuilder(
						currDirtyFields,
						currFormValues,
						currFields,
						key,
						timezone,
						false,
						perilLimitsHidden,
					);

					const [exposureLocator, perilLocator] = key.split('.');

					// fix special peril fields
					const updatedPeril = {
						perilLocator,
						...newUpdatedObject,
					} as PerilUpdateRequest;

					if (
						Object.keys(updatedPeril).length === 2 &&
						updatedPeril.addFieldGroups &&
						updatedPeril.addFieldGroups.length === 0
					) {
						return;
					}

					// check if updateObject has this exposure already
					const updatedExposure = updateObject.updateExposures.find(
						(exposure) => exposure.exposureLocator === exposureLocator,
					);
					if (updatedExposure) {
						// if it does, add the peril to the existing exposure
						Object.keys(updatedPeril).length > 1 &&
							updatedExposure.updatePerils.push(updatedPeril);
					} else {
						// if it doesn't, create a new exposure and add the peril
						const newExposure = {
							exposureLocator,
							updatePerils: [updatedPeril],
						};
						updateObject.updateExposures.push(newExposure);
					}
				} else {
					newUpdatedObject = updateRequestBuilder<PolicyUpdateRequest>(
						currDirtyFields,
						currFormValues,
						currFields,
						key,
						timezone,
					);
					newUpdatedObject = {
						exposureLocator: key,
						...newUpdatedObject,
						updatePerils: [],
					};
					updateObject.updateExposures.push(newUpdatedObject);
				}
			}
		}
	});

	// Remove requests that have no updated in their updatePerils, updateFieldGroups, and fieldValues
	updateObject.updateExposures = updateObject.updateExposures.filter(
		(exposure) =>
			exposure.updatePerils?.length > 0 ||
			exposure.updateFieldGroups?.length > 0 ||
			exposure.addFieldGroups?.length > 0 ||
			exposure.removeFieldGroups?.length > 0 ||
			(exposure.fieldValues && Object.keys(exposure.fieldValues).length > 0),
	);
	updateObject.updateExposures.length === 0 &&
		delete updateObject.updateExposures;

	if (addExposure) {
		updateObject.addExposures = [addExposure];
	}

	if (addPeril) {
		// if any updates to this exposure are pending, append add peril request to that
		const exposureIndex = updateObject?.updateExposures?.findIndex(
			(exposure) => exposure.exposureLocator === addPeril.exposureLocator,
		);

		if (exposureIndex > -1) {
			updateObject.updateExposures[exposureIndex].addPerils = [addPeril.peril];
		} else {
			updateObject.updateExposures
				? updateObject.updateExposures.push({
						exposureLocator: addPeril.exposureLocator,
						addPerils: [addPeril.peril],
				  })
				: (updateObject.updateExposures = [
						{
							exposureLocator: addPeril.exposureLocator,
							addPerils: [addPeril.peril],
						},
				  ]);
		}
	}

	if (removeExposure) {
		updateObject.removeExposures = [removeExposure.exposureLocator];
		const existingExposureUpdates = updateObject?.updateExposures?.findIndex(
			(exposure) => exposure.exposureLocator === removeExposure.exposureLocator,
		);
		if (existingExposureUpdates > -1) {
			// Delete existing exposure updates if they exist
			updateObject.updateExposures.splice(existingExposureUpdates, 1);
		}
	}

	if (removePeril) {
		let existingExposureUpdates = updateObject?.updateExposures?.findIndex(
			(exposure) => exposure.exposureLocator === removePeril.exposureLocator,
		);
		if (existingExposureUpdates > -1) {
			updateObject.updateExposures[existingExposureUpdates].removePerils = [
				removePeril.perilLocator,
			];
			// Check if there are any existing peril updates we need to remove
			const existingPerilUpdates = updateObject?.updateExposures[
				existingExposureUpdates
			]?.updatePerils?.findIndex(
				(peril) => peril.perilLocator === removePeril.perilLocator,
			);
			if (existingPerilUpdates > -1) {
				// Delete existing peril updates if they exist
				updateObject.updateExposures[
					existingExposureUpdates
				].updatePerils.splice(existingPerilUpdates, 1);
			}
		} else {
			updateObject.updateExposures = [
				{
					exposureLocator: removePeril.exposureLocator,
					removePerils: [removePeril.perilLocator],
				},
			];
		}
	}

	if (
		updateObject.fieldValues &&
		Object.keys(updateObject.fieldValues).length === 0
	) {
		delete updateObject.fieldValues;
	}

	return updateObject;
};

export default draftPolicyUpdateBuilder;
